import type { Arc, PieArcDatum } from 'd3-shape';

import {
  _getTextHeight,
  _getTextWidth,
  type TruncationMode,
} from '@dynatrace/strato-components/typography';

import { labelPosition } from './label-position.js';
import { ITEM_FONT_STYLES } from '../../core/components/legend/range/constants.js';
import type { Formatter } from '../../core/types/formatter.js';
import { isInHorizontalBounds } from '../../core/utils/is-in-horizontal-bounds.js';
import { truncateText } from '../../core/utils/text-truncation.js';
import { LABEL_TYPE_OPTION } from '../constants.js';
import type {
  InternalLabel,
  InternalSliceData,
} from '../types/internal/pie-chart.js';
import type { LabelTypeOptions } from '../types/pie-chart.config.js';

export const buildLabel = (
  arc: PieArcDatum<InternalSliceData>,
  path: Arc<unknown, PieArcDatum<InternalSliceData>>,
  valueType: LabelTypeOptions | undefined,
  formatter: Formatter,
  truncationMode: TruncationMode,
  leftCenter: number,
): InternalLabel => {
  const outerRadius = path.outerRadius()(arc);
  const innerRadius = path.innerRadius()(arc);
  const [centroidX, centroidY] = path.centroid(arc);

  const { labelX, labelY } = labelPosition({
    outerRadius,
    innerRadius,
    centroidX,
    centroidY,
  });

  const formattedLabelText = formatLabelText(valueType, formatter, arc.data);
  const labelText = truncateText(
    formattedLabelText,
    truncationMode,
    (truncatedText: string) => {
      const { boxX, boxWidth } = getHorizontalMeasurements(
        truncatedText,
        labelX,
      );
      return isInHorizontalBounds(boxX, boxWidth, leftCenter);
    },
  );

  const horizontalMeasurements = getHorizontalMeasurements(labelText, labelX);
  const verticalMeasurements = getVerticalMeasurements(
    labelX,
    labelText,
    labelY,
  );

  return {
    ...horizontalMeasurements,
    ...verticalMeasurements,
    text: labelText,
  };
};

const formatLabelText = (
  valueType: string | undefined,
  formatter: Formatter,
  data: InternalSliceData,
): string => {
  const labelText =
    valueType === LABEL_TYPE_OPTION.Absolute
      ? formatter(data.value)
      : `${data.relativeValue.toFixed(0)}%`;

  return labelText.trim();
};

const getHorizontalMeasurements = (labelText: string, labelX: number) => {
  const boxWidth = _getTextWidth(labelText, ITEM_FONT_STYLES);
  const textX = labelX + (labelX > 0 ? boxWidth / 2 : -boxWidth / 2);
  const boxX = textX - boxWidth / 2;

  return { boxX, boxWidth, textX };
};

const getVerticalMeasurements = (
  labelX: number,
  labelText: string,
  labelY: number,
) => {
  const labelTextHeight = _getTextHeight(
    labelX >= 0 ? labelText[0] : labelText[labelText.length - 1],
    ITEM_FONT_STYLES,
  );
  const textY = labelY + (labelY > 0 ? labelTextHeight : 0);
  const boxHeight = _getTextHeight(labelText, ITEM_FONT_STYLES);
  const boxY = textY - boxHeight;

  return { boxY, boxHeight, textY };
};
